// @dev TODO originally wanted to make requests polymorphic but currently
// having issues with isomorphic libs. Since NextJS polyfills by default,
// and modern node runtimes are increasingly adopting native fetch support,
// it might be unecessary to handle this at the library level.
// See https://www.builder.io/blog/stop-polyfilling-fetch-in-your-npm-package
import { APIGatewayProxyResultV2 } from 'aws-lambda';

import { AnyObject } from '@sola/schema/dotcom/helpers.js';
import { hash } from '@sola/utils/string.js';

import {
  APPLE_PAY_ALLOWED_LIST,
  REQUESTS_CLIENT_MAX_SECONDS,
  REQUESTS_SERVER_MAX_SECONDS,
} from './constants';
import { getErrorMessage } from './errors-server';
import { UserError } from './errors-user';
import { isObject } from './misc';

const domainAllowlist = [
  ...APPLE_PAY_ALLOWED_LIST,
  '.ci.org',
  '.compassion.com',
  process.env.IS_PROD_ENV ? 'api.tokenex.com' : 'test-api.tokenex.com',
  process.env.IS_SANDBOX_ENV ? '.us-east-2.amazonaws.com' : '',
];

export interface RequestOptions extends RequestInit {
  signRequest?: boolean;
}
export interface GetOptions extends RequestOptions {
  queryParams?: URLSearchParams;
}

interface UserErrors {
  errors: UserError[];
}

export type ErrorResponse = UserErrors | string;

/**
 * Determine if frontend error is a user response/message.
 */
export const isUserError = (e: unknown): e is UserError =>
  isObject(e) && e.type && e.id;

// TODO Not sure if we need this class anymore and can just convert it into an error handler utility.
export class RequestError extends Error {
  public errorType: 'error' | 'request' | 'syntax';

  public response?: Response;

  constructor(error: Error | Response, message: string) {
    super(message);

    if (error instanceof Error) {
      this.errorType = error instanceof SyntaxError ? 'syntax' : 'error';
    } else {
      this.errorType = 'request';
      this.response = error;
    }
  }

  // Use any, because of item's "unpredictable" data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static init = async (error: any) => {
    if (typeof error === 'object' && error?.name === 'TimeoutError') {
      throw new RequestError(error, 'Timeout Error');
    }

    if (error instanceof Error) {
      throw new RequestError(error, error.message);
    }

    if (!(error instanceof Response)) {
      throw new RequestError(error, String(error));
    }

    const errorContent =
      error.headers.get('content-type') === 'application/json'
        ? await error.json()
        : await error.text();

    if (isUserError(errorContent)) {
      throw errorContent;
    }

    throw new RequestError(error, `${error.status}: ${errorContent}`);
  };
}

/**
 * HTTP request.
 */
export const httpRequest = async <T>(
  endpoint: RequestInfo,
  options: RequestOptions,
  // TODO I think we might want to type this out differently.
  // Since client will be catching errors/throwing errors, otherwise should we always just return the expected response:
): Promise<{ response: T | undefined; statusCode: number }> => {
  try {
    const url = new URL(
      typeof endpoint === 'string' ? endpoint : endpoint.url,
      typeof window !== 'undefined' ? window.location.href : undefined,
    );

    if (
      !domainAllowlist.some((domain) => domain && url.hostname.endsWith(domain))
    ) {
      throw new Error('The http request does not contain an allowed domain.');
    }

    // TODO: node's built-in fetch doesn't support agent/dispatcher options.
    // Probably need to install undici directly to enable connection reuse (node uses undici under the covers, but doesn't have all of it)
    // https://undici.nodejs.org/#/?id=undicifetchinput-init-promise
    // https://github.com/nodejs/undici/discussions/2167#discussioncomment-6239415 (could set the global dispatcher... but there are ramifications)
    const { signal, ...fetchOptions } = options;
    const timeout =
      1000 *
      (typeof window === 'undefined'
        ? REQUESTS_SERVER_MAX_SECONDS
        : REQUESTS_CLIENT_MAX_SECONDS);

    // Older Safari doesn't support AbortSignal.timeout
    const timeoutFallback = (ms: number) => {
      const ctrl = new AbortController();
      setTimeout(() => ctrl.abort(), ms);
      return ctrl.signal;
    };

    const response = await fetch(endpoint, {
      signal:
        signal ||
        (typeof AbortSignal?.timeout !== 'undefined'
          ? AbortSignal.timeout(timeout)
          : timeoutFallback(timeout)),
      ...fetchOptions,
    });

    // Is this an unexpected status code?
    if (
      ![200, 201, 202, 204, 207, 400, 401, 402, 404, 409, 410, 429].includes(
        response.status,
      )
    ) {
      // 400: Connect returns this status code for things like child isn't available
      // 402: Payment required - what we throw with payment fail
      // 409: Used for Availability Check
      // 410: Also used for Availability Check
      // 429: Too many attempts

      // TODO: returning the string of the response at least - although this still won't likely trigger some of our errors correctly.
      throw await response.text();
    }

    // Due to how our logic/error handler is working, we need to use response.text() more than once.
    // Since the response body is one-use only, this is the recommended way of making it available again.
    // const throwableResponse = response.clone();
    const retryResponse = response.clone();

    return {
      response: await (response.json().catch(async () => {
        // If the json failed to deserialize, need to use the cloned response to check the text
        return await retryResponse.text().catch((intError) => {
          // Sometimes an empty ok response is expected
          if (response.ok) {
            return '';
          }

          throw intError;
        });
      }) as Promise<T>),
      statusCode: response.status,
    };
  } catch (error: unknown) {
    console.error(
      `httpRequest error for ${endpoint}:`,
      // All of this error cloning smells fishy...
      error instanceof Response ? await error.text() : { error },
    );

    await RequestError.init(error);
  }

  // I don't think logic will ever get here since errors will be thrown, and we should think about how to return consistent responses (see above TODO)
  return { response: undefined, statusCode: 500 };
};

/**
 * Gets an AWS signed header for making POST and PUT requests to protected lambdas.
 * @param data The request data or body object.
 * @returns The AWS header object with a signed hash.
 */
const getAWSSignedRequestHeader = async (data: AnyObject) => {
  if (!data) {
    return;
  }

  const hashSignature = await hash(JSON.stringify(data));
  if (hashSignature) {
    return { 'x-amz-content-sha256': hashSignature || '' };
  }
};

const commonRequest = async <T>(
  endpoint: RequestInfo,
  method: string,
  data?: AnyObject,
  options: RequestOptions = {},
) => {
  const { headers: customHeaders, signRequest, ...fetchOptions } = options;
  const headers = {
    ...customHeaders,
    ...(signRequest && data && (await getAWSSignedRequestHeader(data))),
  };

  return await httpRequest<T>(endpoint, {
    method,
    ...(data && {
      body: data instanceof FormData ? data : JSON.stringify(data),
    }),
    headers,
    ...fetchOptions,
  });
};

/**
 * GET request.
 */
export const httpGetWithStatus = async <T>(
  endpoint: RequestInfo,
  options: GetOptions = {},
) => {
  const { queryParams, ...requestOptions } = options;
  const finalEndpoint = queryParams
    ? `${endpoint}?${new URLSearchParams(queryParams)}`
    : endpoint;

  return await commonRequest<T>(
    finalEndpoint,
    'GET',
    undefined,
    requestOptions,
  );
};

export const httpGet = async <T>(
  endpoint: RequestInfo,
  options: GetOptions = {},
) => {
  const { response } = await httpGetWithStatus<T>(endpoint, options);

  return response;
};

/**
 * POST request.
 */
export const httpPostWithStatus = async <T>(
  endpoint: RequestInfo,
  data: AnyObject,
  options: RequestOptions = {},
) => {
  return await commonRequest<T>(endpoint, 'POST', data, options);
};

/**
 * POST request.
 */
export const httpPost = async <T>(
  endpoint: RequestInfo,
  data: AnyObject,
  options: RequestOptions = {},
) => {
  const { response } = await httpPostWithStatus<T>(endpoint, data, options);

  return response;
};

/**
 * Signed POST request.
 */
export const httpPostSigned = async <T>(
  endpoint: RequestInfo,
  data: AnyObject,
  options: RequestOptions = {},
) => {
  const requestOptions: RequestOptions = {
    ...options,
    signRequest: true,
  };
  return await httpPost<T>(endpoint, data, requestOptions);
};

/**
 * PATCH request including the status code.
 */
export const httpPatchWithStatus = async <T>(
  endpoint: RequestInfo,
  data: AnyObject,
  options: RequestOptions = {},
) => {
  return await commonRequest<T>(endpoint, 'PATCH', data, options);
};

/**
 * PATCH request.
 */
export const httpPatch = async <T>(
  endpoint: RequestInfo,
  data: AnyObject,
  options: RequestOptions = {},
) => {
  const { response } = await httpPatchWithStatus<T>(endpoint, data, options);

  return response;
};

/**
 * PUT request including the status code.
 */
export const httpPutWithStatus = async <T>(
  endpoint: RequestInfo,
  data: AnyObject,
  options: RequestInit = {},
) => {
  return await commonRequest<T>(endpoint, 'PUT', data, options);
};

/**
 * PUT request.
 */
export const httpPut = async <T>(
  endpoint: RequestInfo,
  data: AnyObject,
  options: RequestOptions = {},
) => {
  const { response } = await httpPutWithStatus<T>(endpoint, data, options);

  return response;
};

/**
 * DELETE request including the status code.
 */
export const httpDeleteWithStatus = async <T>(
  endpoint: RequestInfo,
  options: RequestInit = {},
) => {
  return await commonRequest<T>(endpoint, 'DELETE', undefined, options);
};

/**
 * DELETE request.
 */
export const httpDelete = async <T>(
  endpoint: RequestInfo,
  options: RequestOptions = {},
) => {
  const { response } = await httpDeleteWithStatus<T>(endpoint, options);

  return response;
};

// TODO: I don't think the following response utils belong in httpClient, but just sticking to the original organization.

type ApiResponse = Response | APIGatewayProxyResultV2;

type ResOptions = Partial<Omit<Response, 'statusText' | 'headers'>> & {
  /**
   * To support previous implementation. In the future just use status.
   */
  statusCode?: number;
  /**
   * Only useable in a Api Gateway context.
   */
  cookies?: string[];
  /**
   * At this point we're just passing in objects vs Headers instance
   */
  headers?: { [key: string]: string };
};

type HttpResponseObjectArgs = {
  // This is to mimic Response.json()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any;
  contentType?: 'json' | 'text';
  options?: ResOptions;
};

/**
 * Normalizes our response objects across NextJS and API Gateway lambdas.
 */
const httpResponseObject = ({
  body,
  contentType = 'json',
  options,
}: HttpResponseObjectArgs): ApiResponse => {
  const status = options?.status || options?.statusCode || 200;
  const { headers, cookies } = options || {};
  const isJson = contentType === 'json';

  // NextJS responses.
  if (process.env.NEXT_RUNTIME) {
    if (isJson) {
      return Response.json(body, { status, headers });
    }
    return new Response(body, { status, headers });
  }

  // Api Gateway Responses.
  return {
    body: isJson ? JSON.stringify(body) : body,
    cookies,
    headers: {
      ...headers,
      'Content-Type': isJson ? 'application/json' : 'text/plain',
    },
    statusCode: status,
  };
};

/**
 * Utility for our API responses.
 */
export const httpResponse = {
  // Using any to mimic Response.json()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  json: (resBody: any, options?: ResOptions) =>
    httpResponseObject({
      body: resBody,
      options,
    }),

  text: (resBody: string, options?: ResOptions) =>
    httpResponseObject({
      body: resBody,
      contentType: 'text',
      options,
    }),

  /**
   * Use sparingly, ideally we should usually be returning userErrorResponse with specific user messages.
   */
  error: (errorMessage: string, options?: ResOptions) => {
    const { status, statusCode, ...responseOptions } = options || {};

    return httpResponseObject({
      body: {
        message: errorMessage || 'Encountered an unhandled error on the server',
      },
      options: {
        ...responseOptions,
        status: status || statusCode || 500,
      },
    });
  },
};

/**
 * Helper for our API error responses.
 */
export const userErrorResponse = (
  error: unknown,
  cookies?: string[],
): ApiResponse => {
  if (isUserError(error)) {
    const { data, id, type, statusCode, headers } = error;
    const errors: UserErrors = { errors: [{ data, id, type }] };
    return httpResponse.json(errors, {
      ...(statusCode && { status: statusCode }),
      ...(headers && { headers }),
      ...(cookies && { cookies }),
    });
  }
  // TODO This could probably use some love down the road.
  if (isObject(error)) {
    return httpResponse.json(error.message, {
      status: error.statusCode ?? 500,
    });
  }

  return httpResponse.json(getErrorMessage(error), { status: 500 });
};
